import { z } from 'zod'
import { type BookingClient, type BookingInvitee, LocationKind, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type BookingSlot } from './form/timeSlots'

const locationRefSchema = z
  .object({
    kind: z.nativeEnum(LocationKind),
    id: z.string().nullable().optional(),
  })
  .refine((value) => value.kind !== LocationKind.FIXED || Boolean(value.id), { message: 'A fixed location requires an id', path: ['id'] })

const customLocationSchema = z.object({
  name: z.string(),
  address: z.string(),
  note: z.string().optional(),
  timeZone: z.string().optional(),
})

// Domain objects (owner, event type, clients, slot) keep their exact model types via z.custom;
// required-presence and the "pick a time unless the client chooses" rule are enforced as
// object-level refinements so the inferred field types stay cleanly nullable.
export const scheduleFormSchema = z
  .object({
    owner: z.custom<User>().nullable(),
    eventType: z.custom<SchedulingEventType>().nullable(),
    clients: z.array(z.custom<BookingClient>()),
    timeMode: z.enum(['specific-time', 'client-chooses']),
    slot: z.custom<BookingSlot>().nullable(),
    suggestedDate: z.date().nullable(),
    meetingMode: z.nativeEnum(MeetingMode),
    location: locationRefSchema.nullable(),
    note: z.string(),
    title: z.string(),
    titleEdited: z.boolean(),
    invitees: z.array(z.custom<BookingInvitee>()),
    timezone: z.string(),
    customLocation: customLocationSchema.nullable(),
  })
  .refine((data) => data.owner !== null, { message: 'Select an owner', path: ['owner'] })
  .refine((data) => data.eventType !== null, { message: 'Select an event type', path: ['eventType'] })
  .refine((data) => data.clients.length > 0, { message: 'Add at least one client', path: ['clients'] })
  .refine((data) => data.timeMode === 'client-chooses' || data.slot !== null, { message: 'Pick a time', path: ['slot'] })

export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>
